import { toast } from "@/components/ui/use-toast";

interface LoginResponse {
  token: string;
  user?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
}

interface LoginCredentials {
  username: string;
  password: string;
}

export const login = async (credentials: LoginCredentials): Promise<LoginResponse | null> => {
  try {
    console.log("Attempting login with:", credentials.username);
    // Using the disco-auth endpoint for login
    const response = await fetch("https://api.discoveryconciergeservices.com/api/v1/disco-auth/login/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "Login failed");
    }

    const data: LoginResponse = await response.json();
    console.log("Login successful, response:", data);
    
    // Store token in localStorage - NOTE: API returns 'token' as the token
    localStorage.setItem("authToken", data.token);
    
    // If user data is included in response, store it
    if (data.user) {
      localStorage.setItem("user", JSON.stringify(data.user));
    } else {
      // For now, create a simplified user object
      // In a real app, you'd make a separate request to get the user profile
      const userInfo = {
        id: 1,
        email: credentials.username,
        first_name: "User",
        last_name: ""
      };
      localStorage.setItem("user", JSON.stringify(userInfo));
    }
    
    return data;
  } catch (error) {
    console.error("Login error:", error);
    toast({
      title: "Login failed",
      description: error instanceof Error ? error.message : "An unexpected error occurred",
      variant: "destructive",
    });
    return null;
  }
};

export const logout = async (): Promise<boolean> => {
  console.log("Logging out user");
  const token = localStorage.getItem("authToken");
  
  if (token) {
    try {
      // Call the logout endpoint to invalidate the token
      const response = await fetch("https://api.discoveryconciergeservices.com/api/v1/rest-auth/logout/", {
        method: "POST",
        headers: {
          // Use 'Token' prefix as shown in the successful network request
          "Authorization": `Token ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.warn("Logout API call failed, but proceeding with local logout", response.status);
      } else {
        console.log("Logout API call successful");
      }
    } catch (error) {
      console.error("Error during logout API call:", error);
      // We'll still clear local storage even if the API call fails
    }
  }
  
  // Clear local storage regardless of API call result
  localStorage.removeItem("authToken");
  localStorage.removeItem("user");
  localStorage.removeItem("userData");
  
  return true;
};

export const getCurrentUser = (): { id: number; email: string; first_name: string; last_name: string } | null => {
  const userStr = localStorage.getItem("user");
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null;
  }
};

export const isAuthenticated = (): boolean => {
  console.log("Checking if user is authenticated");
  const token = localStorage.getItem("authToken");
  
  if (!token) {
    console.log("No token found, user is not authenticated");
    return false;
  }
  
  // Check if token is non-empty
  if (!token.trim()) {
    console.log("Token is empty, user is not authenticated");
    return false;
  }
  
  // In a production app, you would validate the token more thoroughly
  // For example, checking JWT expiration or making a validation request to the server
  console.log("Token exists, user is authenticated");
  return true;
};

export const validateToken = async (): Promise<boolean> => {
  const token = localStorage.getItem("authToken");
  
  if (!token) {
    return false;
  }

  try {
    // Make a lightweight API call to validate the token
    const response = await fetch("https://api.discoveryconciergeservices.com/api/v1/rest-auth/user/", {
      method: "GET",
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (response.status === 401 || response.status === 403) {
      // Token is invalid, clear local storage
      await logout();
      return false;
    }

    return response.ok;
  } catch (error) {
    console.error("Token validation error:", error);
    return false;
  }
};
